// NumericKeypadSheet — bottom-sheet numeric keypad primitive for the
// capture surface (buildspec 20-ui §5.2).
//
// Why a custom keypad rather than `<input type="number" inputmode="decimal">`:
// on mobile the OS soft keyboard overlays the bottom half of the viewport,
// occluding the capture row the user is trying to edit. A bottom-sheet
// keypad that we draw ourselves sits exactly where the row is, the preview
// shows the value + unit inline, and the sheet only dismisses when the
// user taps Done / Clear / overlay / presses Escape. No <input> element is
// rendered, so the OS keyboard is never invoked.
//
// A11y contract:
//   - role="dialog", aria-modal="true", aria-labelledby (when title is set),
//     or aria-label (otherwise; driven by the ariaLabel prop).
//   - Body-level siblings get `inert` + `aria-hidden="true"` while open and
//     are restored to their prior state on close (same pattern as Dialog).
//   - Esc closes.
//   - Overlay click closes.
//   - Tab / Shift+Tab cycle within the sheet (focus trap).
//   - Hardware keyboard: 0-9 append, '.' appends (if `decimal`), Backspace
//     removes last char, Enter calls Done. This makes the primitive usable
//     without touch AND lets us keep assertive RTL-only tests simple.
//   - Every key button meets the 44×44 px touch-target invariant.
//
// Value contract:
//   - `value` is a canonical decimal string (e.g. "", "3", "3.5", "3.50").
//   - `onChange(next)` fires when the value actually changes. Redundant
//     presses (backspace on empty, 3rd decimal digit, duplicate '.') do NOT
//     emit. This matches "empty → no emit" in the C2.10 acceptance note.
//   - Tapping Clear emits `onChange("")` — an explicit clear is different
//     from a no-op and parents (e.g. MeasurementInput) rely on it.
//   - A lone '.' is auto-prefixed to "0." for legibility — "0." is the
//     canonical form when the user started with a decimal point.
//
// Display draft (preserve user-typed precision while editing):
//   The sheet keeps an internal `draft` string that mirrors what the user
//   has tapped. It is seeded from `value` on every open transition and
//   from then on it diverges: each digit / decimal / backspace / clear
//   updates the draft locally AND fires `onChange(draft)` upward so the
//   parent's per-keystroke dispatch (server PUT, zero-confirm reversion,
//   etc.) keeps working.
//
//   Why a draft instead of reading `value` straight through: the parent
//   often round-trips the value through Postgres `numeric(10,2)`, which
//   pads decimals — typing "5.8" eventually echoes back as "5.80" because
//   that's how the column stores it. Without a local draft, the keypad's
//   preview and operations would re-render against "5.80" mid-edit, so
//   the user sees their "5.8" silently grow a trailing zero. The draft
//   pins the visible state to exactly what was typed for the duration of
//   the open sheet; the next open re-syncs from `value` (the canonical
//   stored form), which is the correct view after committing.
//
//   Count inputs (`decimal={false}`) are the inverse: the parent
//   canonicalizes every keypress (e.g. AssetRow's `parseCountQuantity`
//   strips a leading zero so "07" → "7"). Pinning the draft would let it
//   drift from the canonical stored value — tapping `7` from a zero
//   count would leave the preview as "07", and the next digit would
//   build off the stale draft ("072" while the row says 72). For
//   non-decimal mode we therefore re-sync the draft from `value` on
//   every prop change while the sheet is open, so the canonical form
//   always wins. Decimal mode keeps its divergence behaviour because
//   integer canonicalization doesn't apply.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

export interface NumericKeypadSheetProps {
  open: boolean
  onClose: () => void
  value: string
  onChange: (next: string) => void
  /** Render the '.' key. Default true. */
  decimal?: boolean
  /** Max digits after the decimal point. Default 2. */
  maxDecimals?: number
  /** Unit suffix shown next to the value preview (e.g. "m²"). */
  unit?: string
  /** Accessible label for the dialog when no title is provided. */
  ariaLabel: string
  /** Optional heading shown above the preview. */
  title?: string
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

// Hard cap on the typed string length. 7 chars covers the realistic
// ceiling for every UoM in use (`9999.99` m²/m³, `9999999` Count, etc.)
// and prevents the preview text from overflowing the sheet horizontally.
const MAX_INPUT_LENGTH = 7

function getFocusableWithin(root: HTMLElement): HTMLElement[] {
  const nodes = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  return Array.from(nodes).filter((el) => !el.hasAttribute('disabled'))
}

function canAppendDigit(
  current: string,
  maxDecimals: number,
): boolean {
  if (current.length >= MAX_INPUT_LENGTH) return false
  const dotIdx = current.indexOf('.')
  if (dotIdx === -1) return true
  const decimals = current.length - dotIdx - 1
  return decimals < maxDecimals
}

export function NumericKeypadSheet({
  open,
  onClose,
  value,
  onChange,
  decimal = true,
  maxDecimals = 2,
  unit,
  ariaLabel,
  title,
}: NumericKeypadSheetProps) {
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const invokerRef = useRef<HTMLElement | null>(null)
  const [portalHost] = useState<HTMLDivElement | null>(() => {
    if (typeof document === 'undefined') return null
    const host = document.createElement('div')
    host.setAttribute('data-numeric-keypad-root', '')
    return host
  })

  // Local draft mirroring the user's typed string. See the file header
  // "Display draft" block for the full rationale; in short, decimal mode
  // pins this draft so Postgres `numeric(10,2)` round-trips
  // ("5.8" → "5.80") cannot rewrite the preview while the user is
  // mid-edit.
  //
  // Count inputs (`decimal={false}`) skip the draft entirely — the
  // parent canonicalizes every keystroke (AssetRow's
  // `parseCountQuantity` strips leading zeros: "07" → "7"), and the
  // canonical form must always win to keep the preview in lock-step
  // with the stored count. Reading `value` directly here also avoids
  // the React anti-pattern of cascading setState calls inside an
  // effect to sync external state into local state.
  const [draftStorage, setDraftStorage] = useState(value)
  const wasOpenRef = useRef(open)
  // Gates the pure-zero replace below. Resets false on every open;
  // flips true on the first `commit()`. Without it the regex would
  // hijack mid-entry "0.0" toward 0.05 (Codex P1 on PR #544).
  const hasTypedRef = useRef(false)
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraftStorage(value)
      hasTypedRef.current = false
    }
    wasOpenRef.current = open
  }, [open, value])
  const draft = decimal === false ? value : draftStorage

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const commit = useCallback(
    (next: string) => {
      hasTypedRef.current = true
      setDraftStorage(next)
      onChange(next)
    },
    [onChange],
  )

  const appendDigit = useCallback(
    (digit: string) => {
      // First-tap-after-open against a SEEDED pure-zero draft REPLACES
      // (unwedges numeric(10,2) round-trips). See zero-padded-replace
      // test for the contract + Codex P1 mid-entry guard.
      if (!hasTypedRef.current && /^0+(\.0+)?$/.test(draft)) {
        commit(digit)
        return
      }
      if (!canAppendDigit(draft, maxDecimals)) return
      let next = draft + digit
      if (next.length > 1 && next.startsWith('0') && next[1] !== '.') {
        next = next.replace(/^0+/, '') || '0'
      }
      commit(next)
    },
    [commit, draft, maxDecimals],
  )

  const appendDecimal = useCallback(() => {
    if (!decimal) return
    if (draft.includes('.')) return
    const next = draft === '' ? '0.' : draft + '.'
    if (next.length > MAX_INPUT_LENGTH) return
    commit(next)
  }, [commit, decimal, draft])

  const backspace = useCallback(() => {
    if (draft === '') return
    commit(draft.slice(0, -1))
  }, [commit, draft])

  const clear = useCallback(() => {
    if (draft === '') return
    commit('')
  }, [commit, draft])

  // Portal host mount / unmount on open.
  useEffect(() => {
    if (!open) return undefined
    const host = portalHost
    if (host === null) return undefined
    if (!document.body.contains(host)) {
      document.body.appendChild(host)
    }
    return () => {
      if (document.body.contains(host)) {
        document.body.removeChild(host)
      }
    }
  }, [open, portalHost])

  // Focus + sibling isolation — mirrors Dialog's contract so both
  // primitives feel identical to AT users.
  useEffect(() => {
    if (!open) return undefined

    const previouslyFocused = document.activeElement
    invokerRef.current =
      previouslyFocused instanceof HTMLElement ? previouslyFocused : null

    const root = portalHost
    const sheet = sheetRef.current
    if (root === null || sheet === null) return undefined

    type SiblingSnapshot = {
      el: HTMLElement
      hadInert: boolean
      prevAriaHidden: string | null
    }
    const touched: SiblingSnapshot[] = []
    for (const child of Array.from(document.body.children)) {
      if (child === root) continue
      if (!(child instanceof HTMLElement)) continue
      touched.push({
        el: child,
        hadInert: child.hasAttribute('inert'),
        prevAriaHidden: child.getAttribute('aria-hidden'),
      })
      child.setAttribute('inert', '')
      child.setAttribute('aria-hidden', 'true')
    }

    const focusables = getFocusableWithin(sheet)
    const firstFocusable = focusables[0]
    if (firstFocusable !== undefined) {
      firstFocusable.focus()
    } else {
      sheet.focus()
    }

    return () => {
      for (const snap of touched) {
        if (!snap.hadInert) snap.el.removeAttribute('inert')
        if (snap.prevAriaHidden === null) {
          snap.el.removeAttribute('aria-hidden')
        } else {
          snap.el.setAttribute('aria-hidden', snap.prevAriaHidden)
        }
      }
      const invoker = invokerRef.current
      if (invoker !== null && document.contains(invoker)) {
        invoker.focus()
      }
      invokerRef.current = null
    }
  }, [open, portalHost])

  // Global key handling: Escape always closes; digit / '.' / Backspace /
  // Enter route to the same handlers the buttons use, so users with a
  // hardware keyboard (or Vitest) get identical semantics.
  useEffect(() => {
    if (!open) return undefined
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        handleClose()
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        handleClose()
        return
      }
      if (e.key === 'Backspace') {
        e.preventDefault()
        backspace()
        return
      }
      if (e.key === '.') {
        e.preventDefault()
        appendDecimal()
        return
      }
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault()
        appendDigit(e.key)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, handleClose, backspace, appendDecimal, appendDigit])

  if (!open || portalHost === null) return null

  const handleSheetKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return
    const sheet = sheetRef.current
    if (sheet === null) return
    const focusables = getFocusableWithin(sheet)
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (first === undefined || last === undefined) {
      e.preventDefault()
      sheet.focus()
      return
    }
    const active = document.activeElement
    if (e.shiftKey) {
      if (active === first || !sheet.contains(active)) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (active === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  const handleOverlayClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  const titleId = title !== undefined ? 'numeric-keypad-title' : undefined
  const previewText = draft === '' ? '—' : draft
  const previewUnit = unit !== undefined && unit !== '' ? ` ${unit}` : ''

  const digitKeys: Array<{ label: string; value: string }> = [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
  ]

  const digitButtonClass =
    'inline-flex min-h-14 min-w-14 items-center justify-center rounded-lg border border-gray-200 bg-white text-xl font-semibold text-gray-900 active:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)]'

  // Decimal key gets the same prominence as a digit (white bg, bold)
  // because for m/m²/m³ assets it's tapped as often as any number.
  // The glyph itself is bumped up — a `.` at digit-key text size is
  // hard to spot on mobile, which is why surveyors couldn't find it.
  const decimalButtonClass =
    'inline-flex min-h-14 min-w-14 items-center justify-center rounded-lg border border-gray-200 bg-white text-4xl font-bold leading-none text-gray-900 pb-2 active:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)] disabled:opacity-40 disabled:cursor-not-allowed'

  const auxButtonClass =
    'inline-flex min-h-14 min-w-14 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-base font-medium text-gray-800 active:bg-gray-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)] disabled:opacity-40 disabled:cursor-not-allowed'

  const doneButtonClass =
    'inline-flex min-h-14 min-w-14 items-center justify-center rounded-lg bg-[color:var(--color-brand)] text-base font-semibold text-[color:var(--color-brand-foreground)] active:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)]'

  let dialogAriaProps: Record<string, string>
  if (titleId !== undefined) {
    dialogAriaProps = { 'aria-labelledby': titleId }
  } else {
    dialogAriaProps = { 'aria-label': ariaLabel }
  }

  const keypadContent: ReactNode = (
    <>
      <div
        data-testid="numeric-keypad-overlay"
        onClick={handleOverlayClick}
        className="fixed inset-0 z-40 bg-black/50"
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        {...dialogAriaProps}
        tabIndex={-1}
        onKeyDown={handleSheetKeyDown}
        data-testid="numeric-keypad-sheet"
        className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-gray-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl focus:outline-none"
      >
        {title !== undefined ? (
          <h2
            id={titleId}
            className="mb-2 text-sm font-semibold text-gray-700"
          >
            {title}
          </h2>
        ) : null}
        <div
          data-testid="numeric-keypad-preview"
          className="mb-3 flex min-h-14 items-center justify-end overflow-hidden rounded-md bg-gray-50 px-4 py-2 text-3xl font-semibold tabular-nums text-gray-900"
        >
          <span className="whitespace-nowrap">
            {previewText}
            {previewUnit}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {digitKeys.map((key) => (
            <button
              key={key.value}
              type="button"
              aria-label={`Enter ${key.label}`}
              onClick={() => appendDigit(key.value)}
              className={digitButtonClass}
            >
              {key.label}
            </button>
          ))}
          {decimal ? (
            <button
              type="button"
              aria-label="Enter decimal point"
              onClick={appendDecimal}
              disabled={draft.includes('.')}
              className={decimalButtonClass}
            >
              .
            </button>
          ) : (
            <span aria-hidden="true" />
          )}
          <button
            type="button"
            aria-label="Enter 0"
            onClick={() => appendDigit('0')}
            className={digitButtonClass}
          >
            0
          </button>
          <button
            type="button"
            aria-label="Backspace"
            onClick={backspace}
            disabled={draft === ''}
            className={auxButtonClass}
          >
            ⌫
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={clear}
            disabled={draft === ''}
            className={`${auxButtonClass} flex-1`}
          >
            Clear
          </button>
          <button
            type="button"
            onClick={handleClose}
            className={`${doneButtonClass} flex-1`}
          >
            Done
          </button>
        </div>
      </div>
    </>
  )

  return createPortal(keypadContent, portalHost)
}
